import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, ExternalLink, Target, Clock, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import PageMeta from "@/components/PageMeta";
import SocialShareButtons from "@/components/SocialShareButtons";
import { AiGeneratedBadge } from "@/components/AiGeneratedBadge";

const CATEGORY = "daily-pick-analysis";

interface PickArticle {
  id: string;
  slug: string;
  headline: string;
  summary: string | null;
  content: string;
  primary_token: string | null;
  opportunity_score: number | null;
  ai_recommendation: string | null;
  reading_time: number | null;
  created_at: string;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export default function DailyPick() {
  const { data: picks = [], isLoading } = useQuery({
    queryKey: ["daily-pick-articles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articles")
        .select(
          "id, slug, headline, summary, content, primary_token, opportunity_score, ai_recommendation, reading_time, created_at",
        )
        .eq("category", CATEGORY)
        .eq("status", "published")
        .order("created_at", { ascending: false })
        .limit(15);
      if (error) throw error;
      return (data ?? []) as PickArticle[];
    },
    staleTime: 1000 * 60 * 10,
  });

  const latest = picks[0];
  const previous = picks.slice(1);

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title={
          latest
            ? `${latest.headline} | GoToCoins Daily Pick`
            : "Daily Pick | GoToCoins"
        }
        description={
          latest?.summary ??
          "The latest GoToCoins Daily Pick: one AI-selected Australian-tradeable crypto token, published only when it scores 85 or higher."
        }
        path="/daily-pick"
        type="article"
      />

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <header className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">
            Daily Pick
          </p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
            Today's Daily Pick
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            One token per day, in Australian dollars — published only when it scores 85 or
            higher out of 100. If nothing clears the bar, nothing is published. Never financial
            advice.
          </p>
        </header>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-72 w-full" />
          </div>
        ) : !latest ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-muted-foreground" />
                No pick published yet
              </CardTitle>
              <CardDescription>
                No token has cleared the 85-point threshold recently. Check the live movers and
                research in the meantime.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button asChild variant="outline">
                <Link to="/opportunities">Browse opportunities</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/research">Read research</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Short summary card */}
            <Card className="mb-8 border-primary/20 bg-primary/5">
              <CardHeader>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {latest.primary_token && (
                    <Badge variant="outline" className="font-semibold">
                      {latest.primary_token}
                    </Badge>
                  )}
                  {latest.opportunity_score != null && (
                    <Badge variant="secondary">Score {Math.round(latest.opportunity_score)}/100</Badge>
                  )}
                  {latest.ai_recommendation && (
                    <Badge variant="outline">{latest.ai_recommendation.replace(/_/g, " ")}</Badge>
                  )}
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {formatDate(latest.created_at)}
                  </span>
                  {latest.reading_time && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {latest.reading_time} min read
                    </span>
                  )}
                </div>
                <CardTitle className="text-2xl leading-snug">{latest.headline}</CardTitle>
                {latest.summary && (
                  <CardDescription className="text-base leading-relaxed mt-2">
                    {latest.summary}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="flex flex-wrap items-center gap-3">
                {latest.primary_token && (
                  <Button asChild size="sm">
                    <Link to={`/coin-reviewer?symbol=${encodeURIComponent(latest.primary_token)}`}>
                      Run a full review
                      <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Link>
                  </Button>
                )}
                <Button asChild size="sm" variant="outline">
                  <Link to={`/articles/${latest.slug}`}>
                    Open article page
                    <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                  </Link>
                </Button>
                <SocialShareButtons
                  url={`${window.location.origin}/daily-pick`}
                  title={latest.headline}
                  description={latest.summary ?? ""}
                  showLabel={false}
                />
              </CardContent>
            </Card>

            <AiGeneratedBadge />

            {/* Full article */}
            <article
              className="prose prose-lg max-w-none mt-6
                prose-headings:font-bold prose-headings:tracking-tight
                prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl
                prose-p:text-foreground/90 prose-p:leading-relaxed
                prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                prose-strong:text-foreground
                prose-li:text-foreground/90
                prose-blockquote:border-l-4 prose-blockquote:border-primary/50 prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-muted-foreground"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{latest.content}</ReactMarkdown>
            </article>

            {previous.length > 0 && (
              <section className="mt-12">
                <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                  Previous picks
                </h2>
                <ul className="space-y-2">
                  {previous.map((p) => (
                    <li key={p.id}>
                      <Link
                        to={`/articles/${p.slug}`}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3 hover:bg-muted/50 transition-colors"
                      >
                        <span className="text-sm font-medium">{p.headline}</span>
                        <span className="flex items-center gap-2 text-xs text-muted-foreground">
                          {p.opportunity_score != null && (
                            <Badge variant="outline" className="text-[10px]">
                              {Math.round(p.opportunity_score)}/100
                            </Badge>
                          )}
                          {formatDate(p.created_at)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
